import { useTranslation } from "react-i18next";

import React, { useRef } from "react";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Link } from "react-router-dom";
import placeholder from "assests/courses/1.jpg";
import { CaretLeft, CaretRight, BookOpen } from "@phosphor-icons/react";
import { cardThumb } from "Utilities/courseImages";

// Arrow button matches the site's shelf-header convention (Categories page).
const ArrowButton = React.forwardRef(({ label, children }, ref) => (
  <button
    ref={ref}
    aria-label={label}
    className="w-8 h-8 rounded-full border bg-white/5 hover:bg-white/15 border-white/10 text-white flex items-center justify-center transition-colors"
  >
    {children}
  </button>
));

const CourseSlider = ({ data }) => {
  const { i18n, t } = useTranslation();
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const swiperRef = useRef(null);
  const isRTL = i18n.language === "ar";

  return (
    <div className={`mt-10 ${data?.length === 0 ? "hidden" : ""}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen size={20} className="text-primary" weight="fill" />
          <h2 className="text-xl lg:text-2xl font-bold text-white">
            {t("general.instructor_courses")}
          </h2>
        </div>
        <div dir="ltr" className="flex items-center gap-2">
          <ArrowButton ref={prevRef} label={isRTL ? "Next" : "Previous"}>
            <CaretLeft size={14} />
          </ArrowButton>
          <ArrowButton ref={nextRef} label={isRTL ? "Previous" : "Next"}>
            <CaretRight size={14} />
          </ArrowButton>
        </div>
      </div>

      <div className="relative">
        {isRTL && (
          <div className="absolute top-0 left-0 z-[10] h-full w-[80px] bg-gradient-to-r from-bg to-transparent pointer-events-none" />
        )}

        <Swiper
          dir={isRTL ? "rtl" : "ltr"}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          spaceBetween={20}
          navigation={{
            clickable: true,
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
          }}
          slidesPerView="auto"
          modules={[Navigation]}
        >
          {data?.map((course, index) => (
            <SwiperSlide key={index} className="!w-[280px] lg:!w-[320px]">
              <Link to={`/course/${course.slug}`} className="group block">
                <div className="relative rounded-xl overflow-hidden transition-shadow duration-300 group-hover:shadow-[0_0_30px_rgba(255,255,255,0.25)]">
                  <img
                    src={cardThumb(course) || placeholder}
                    alt={course.name}
                    className="w-full h-[160px] lg:h-[180px] object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
                </div>
                <p className="text-white font-medium mt-3 line-clamp-1 group-hover:text-primary transition-colors duration-300">
                  {course.name}
                </p>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>

        {!isRTL && (
          <div className="absolute top-0 right-0 z-[10] h-full w-[80px] bg-gradient-to-l from-bg to-transparent pointer-events-none" />
        )}
      </div>
    </div>
  );
};

export default CourseSlider;
